import { SplitLayout } from "@solomon-apps/shared/src/components/console";
import {
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import type { BeneficialOwnershipPayload } from "@/services/clientOnboarding";
import { fetchClientWallets, type WalletView } from "@/services/clientWallets";

/** One draft UBO entry — the FE-side companion to the BE entries[]. */
interface UboDraftEntry {
    entryUuid: string;
    legalName: string;
}

import { hasUseCaseFormDraft } from "./forms/useCaseFormShared";
import { useOnboardingApplicationContextOrNull } from "./OnboardingApplicationContext";
import {
    OnboardingDraftProvider,
    useOnboardingDraftIds,
    useSetOnboardingDraft
} from "./onboardingDraftContext";
import {
    OnboardingFormStateProvider,
    useUseCaseFormState
} from "./onboardingFormStateContext";
import {
    buildSlotNumberMap,
    findNextIncompleteTarget,
    isSlotVisible,
    slotKey
} from "./OnboardingShared";
import {
    EmptyDetailPanel,
    SlotDetailPanel,
    SubsectionDetailPanel
} from "./panel/DetailPanel";
import { SubsectionGroupRows } from "./panel/ListRows";
import {
    itemListSectionStatus,
    sectionStatusWithDraftActivity
} from "./stepReadiness";
import {
    type ApplicationPhase,
    buildOnboardingTree,
    CATEGORY_ORDER,
    groupByCategory,
    type NextStepTarget,
    type OnboardingNode,
    type OnboardingSubsection,
    type SubsectionId
} from "./types";
import {
    reconcileWalletEntries,
    replaceWalletEntry,
    type WalletEntry
} from "./walletEntries";

/**
 * Onboarding panel — the interactive surface mounted inside `OnboardingShell`
 * at `/onboarding`. 2-column 45/55 layout via `<SplitLayout>`.
 *
 * Left column: every deliverable as its own row — top-level subsections AND
 * their nested children — with each subsection's slots rendered immediately
 * below as numbered sub-rows. Slot numbering continues into nested children
 * (so Entity Standing's slots are 2.1–2.4 and Beneficial Ownership's slots
 * are 2.5–…). Conditional slots stay hidden until the scenario flips the
 * matching trigger on.
 *
 * Right column: context-aware. Renders the focused subsection's full detail
 * — title, status, description, preconditions, primary CTA — when a
 * subsection row is focused. Renders slot-level detail — step number,
 * description, document-to-submit block, upload CTA — when a slot row is
 * focused. Sticky so it stays visible as the user scrolls the list.
 *
 * Below `lg`, the right column hides and the list goes full-width.
 */
export function OnboardingPanel(
    props: Parameters<typeof OnboardingPanelInner>[0]
): ReactElement {
    // Hub-scoped draft state: form-state context is the source of truth
    // for every form's field values (survives navigation between steps),
    // and the draft context tracks which sections have unsaved edits so
    // the left rail can surface "In progress". Mounted here so all 8
    // forms render inside both providers. The form-state provider reads
    // from OnboardingApplicationContext (mounted above in the hub) for
    // server hydration, falling back to empty state when there's no
    // live application.
    return (
        <OnboardingFormStateProvider>
            <OnboardingDraftProvider>
                <OnboardingPanelInner {...props} />
            </OnboardingDraftProvider>
        </OnboardingFormStateProvider>
    );
}

function OnboardingPanelInner({
    subsections,
    applicationFrozen,
    applicationPhase,
    onOpen,
    initialFocusId,
    triggers,
    rightOverride,
    celebratingIds
}: {
    subsections: OnboardingSubsection[];
    applicationFrozen: boolean;
    /** Phase-scoped vocabulary for status badges — see
     *  ApplicationPhase for the split. Threaded down to every
     *  StatusBadge in the hub so a single SectionStatus value never
     *  shows two different labels at once. */
    applicationPhase: ApplicationPhase;
    onOpen: (id: SubsectionId) => void;
    initialFocusId?: SubsectionId;
    triggers?: Record<string, boolean>;
    /** Replaces the right-side detail panel content. Used by the hub to
     *  swap in the celebration card after the approval stagger
     *  animation completes. */
    rightOverride?: ReactNode;
    /** During the approval celebration, ids of sections whose row badge
     *  has been swapped for the row-tall success checkmark. Walks
     *  top-to-bottom in scenario order. Undefined outside the
     *  celebration. */
    celebratingIds?: Set<SubsectionId>;
}): ReactElement {
    /**
     * Solomon-run subsections (sanctions screening, future server-side
     * checks) aren't user-actionable steps — they fire on submission and
     * the applicant just sees their state move to pending. Filter them out
     * before building the tree so they never render as a numbered step or
     * occupy a slot in the numbering. The data still lives on the scenario
     * so the eventual backend handoff has it.
     */
    /**
     * Hub-level mutation state: the user adds wallets / beneficiaries
     * one at a time via the parent panel's "+ Add" CTA. Each click pushes
     * a synthetic slot onto the relevant subsection. The eventual
     * onboarding API call would persist the new record back to scenario
     * data; for the prototype the count lives entirely in this state.
     */
    const context = useOnboardingApplicationContextOrNull();
    const persistedUboStep = context?.stepsByName.beneficial_ownership;
    /** Triggers a wallet re-fetch whenever the wallet_declarations step
     *  changes — most importantly, after WalletForm.handleVerify causes
     *  the BE to flip the step to Complete and the context refetches.
     *  Without this, the draft sentinel slot would persist past
     *  verification and the verified-wallet row would never appear in
     *  the synthetic slot list until the user reloaded the page. */
    const walletStepUpdatedAt =
        context?.stepsByName.wallet_declarations?.updatedAt ?? null;

    /**
     * UBO list as a single typed array. Each entry carries the stable
     * `entryUuid` (generated FE-side on "+ Add Person" or hydrated from
     * BE on mount) + the working `legalName` shown in the left-list
     * synthetic slot title. Splice-by-position handlers replace the
     * earlier parallel-array shape — eliminates the UBO-delete-index
     * bug class by construction (one array, one source of truth).
     */
    const [uboEntries, setUboEntries] = useState<UboDraftEntry[]>([]);
    /**
     * Verified-wallet list. Sourced from Participant Account records via
     * `fetchClientWallets()` on mount (Participant Account is the authority —
     * see Phase C). The list drives the left-list synthetic slots
     * + the auto-completion of the `wallet_declarations` step.
     */
    const [walletEntries, setWalletEntries] = useState<WalletEntry[]>([]);
    /** Subsection IDs the user has explicitly marked complete via a
     *  Submit / Save action in their detail form. Overrides the
     *  scenario-driven status so the StatusBadge flips to Complete and
     *  the form locks. Persistence-on-real-API would push these to the
     *  scenario layer; for the prototype it's hub-local. */
    const [userCompletedIds, setUserCompletedIds] = useState<Set<SubsectionId>>(
        new Set()
    );
    /** Hoisted Use Case form state. Now lives in the form-state context
     *  (not local hub state) so the form's selections + lastSubmitted
     *  snapshot survive navigation away from the Use Case panel without
     *  prop-threading through DetailPanel. */
    const [useCaseState] = useUseCaseFormState();

    /**
     * Hydrate wallet state from the server on mount. Persistence of
     * verified wallets lives on Participant Account records now (see
     * claude-docs/onboarding/squads-wallet-verification.md in yaas-api);
     * local `walletCount` / `walletLabels` are a session cache. Without
     * this hydration, a page refresh mid-onboarding would lose the
     * count and any subsequent re-add would hit
     * `WALLET_ALREADY_REGISTERED`. Best-effort: swallow errors and fall
     * back to the local state so a transient API blip doesn't block
     * onboarding entirely.
     */
    useEffect(() => {
        let cancelled = false;
        void fetchClientWallets()
            .then((res) => {
                if (cancelled) return;
                setWalletEntries((prev) =>
                    reconcileWalletEntries(prev, res.data)
                );
            })
            .catch(() => {
                // Silent fall-back to local state — surfacing a banner
                // here would block the rest of onboarding for a problem
                // that doesn't affect any other section.
            });
        return (): void => {
            cancelled = true;
        };
    }, [walletStepUpdatedAt]);

    // Hydrate UBO list from the persisted backend payload on mount.
    // Each entry's `entryUuid` and `legalName` flow into the synthetic
    // slot list; the rest of the per-entry data
    // (governmentIdUuid / ownershipPercent) is read by PersonDetailForm
    // on demand via context.
    useEffect(() => {
        const payload = persistedUboStep?.payload as
            | Partial<BeneficialOwnershipPayload>
            | undefined;
        const entries = payload?.entries ?? [];
        if (entries.length === 0) return;
        setUboEntries(
            entries.map((e) => ({
                entryUuid: e.entryUuid,
                legalName: e.legalName ?? ""
            }))
        );
    }, [persistedUboStep]);

    const draftIds = useOnboardingDraftIds();

    // Use Case form is hub-hoisted (it unmounts when another section is
    // focused), so report its unsaved-edit state from here rather than
    // from inside the form, which other forms do via
    // `useReportOnboardingDraft`.
    const useCaseHasDraft = useMemo(
        () => hasUseCaseFormDraft(useCaseState),
        [useCaseState]
    );
    useSetOnboardingDraft("use-case", useCaseHasDraft);

    const visibleSubsections = useMemo(() => {
        const filtered = subsections.filter(
            (s) => s.treatment !== "solomon-run"
        );
        return filtered.map((original) => {
            // Apply user-driven completion override first so downstream
            // synthetic-slot logic operates on the corrected status.
            let s = original;
            if (userCompletedIds.has(s.id)) {
                s = { ...s, status: "Complete" as const };
            }
            if (s.id === "wallet-primary") {
                const walletCount = walletEntries.length;
                const verifiedWalletCount = walletEntries.filter(
                    (entry) => entry.wallet !== null
                ).length;
                // Empty list / nothing verified → Not started; draft-only
                // rows → In progress; canonical `itemListSectionStatus`.
                const displayStatus = itemListSectionStatus(
                    s.status,
                    walletCount,
                    verifiedWalletCount
                );
                return {
                    ...s,
                    status: sectionStatusWithDraftActivity(
                        displayStatus,
                        draftIds.has(s.id)
                    ),
                    slots: walletEntries.map((entry, i) => {
                        const typed = entry.label.trim();
                        return {
                            title: typed || `Wallet ${i + 1}`,
                            description:
                                "One form per wallet. Pick the type (Multisig, Account, or Program), enter the destination address, add an optional label, and complete the matching control proof."
                        };
                    }),
                    totalProofPoints: walletCount,
                    multiplierLabel:
                        walletCount > 1
                            ? `×${walletCount} wallet declarations`
                            : undefined
                };
            }
            if (s.id === "beneficial-ownership") {
                const uboCount = uboEntries.length;
                const persistedEntries =
                    (
                        persistedUboStep?.payload as
                            | Partial<BeneficialOwnershipPayload>
                            | undefined
                    )?.entries ?? [];
                // Empty payload → Not started even if the BE step row is
                // Complete; draft-only rows → In progress. Mirrors the
                // empty-UBO-not-complete guard in `scenarioFromServer`.
                const displayStatus = itemListSectionStatus(
                    s.status,
                    uboCount,
                    persistedEntries.length
                );
                return {
                    ...s,
                    status: sectionStatusWithDraftActivity(
                        displayStatus,
                        draftIds.has(s.id)
                    ),
                    slots: uboEntries.map((entry, i) => {
                        const typed = entry.legalName.trim();
                        return {
                            title: typed || `Person ${i + 1}`,
                            description:
                                "Beneficiary's full legal name, ownership percentage, and a government photo ID. We screen the name against sanctions lists on our end."
                        };
                    }),
                    totalProofPoints: uboCount,
                    multiplierLabel:
                        uboCount > 1 ? `×${uboCount} beneficiaries` : undefined
                };
            }
            // (Tax-identification slot filtering removed per BD-aligned
            // trim — Section 3 is now a single "Tax ID" upload slot.)
            return {
                ...s,
                status: sectionStatusWithDraftActivity(
                    s.status,
                    draftIds.has(s.id)
                )
            };
        });
    }, [
        subsections,
        walletEntries,
        uboEntries,
        userCompletedIds,
        persistedUboStep,
        draftIds
    ]);
    const tree = useMemo(
        () => buildOnboardingTree(visibleSubsections),
        [visibleSubsections]
    );
    const grouped = useMemo(
        () => groupByCategory(tree, (node) => node.subsection.category),
        [tree]
    );
    /**
     * Sequential ordinal per top-level subsection — drives the visible 1–N
     * numbering on the left list. Children render un-numbered in the list.
     */
    const ordinalById = useMemo(() => {
        const map = new Map<SubsectionId, number>();
        let n = 1;
        for (const cat of CATEGORY_ORDER) {
            const list = grouped.get(cat);
            if (!list) continue;
            for (const node of list) map.set(node.subsection.id, n++);
        }
        return map;
    }, [grouped]);

    /**
     * Slot numbering — `${parentTopLevelOrdinal}.${counter}` per slot.
     * Children share the parent's namespace so the counter continues across
     * the nested boundary. See `buildSlotNumberMap` in OnboardingShared.
     */
    const slotNumberByKey = useMemo(
        () => buildSlotNumberMap(tree, triggers),
        [tree, triggers]
    );

    // Default focus — first top-level subsection (subsection ordinal 1).
    const defaultFocusId = initialFocusId ?? tree[0]?.subsection.id;
    const [focusedId, setFocusedId] = useState<SubsectionId | null>(
        defaultFocusId ?? null
    );
    const [focusedSlot, setFocusedSlot] = useState<{
        parentId: SubsectionId;
        slotIdx: number;
    } | null>(null);

    useEffect(() => {
        setFocusedId(defaultFocusId ?? null);
        setFocusedSlot(null);
    }, [defaultFocusId]);

    const focusedNode = useMemo<OnboardingNode | null>(() => {
        if (!focusedId) return null;
        for (const node of tree) {
            if (node.subsection.id === focusedId) return node;
            const child = node.children.find((c) => c.id === focusedId);
            if (child) {
                return { subsection: child, children: [] };
            }
        }
        return null;
    }, [tree, focusedId]);

    /**
     * Auto-collapse rule: a subsection that has no nested children, isn't
     * in `slotsAsItems` (list-mode) mode, and has exactly one *visible*
     * slot acts as a single-action subsection. The slot row is suppressed
     * in the left list and clicking the parent row routes the right panel
     * straight to the slot detail. When a trigger flips on more
     * conditional slots, the subsection un-collapses automatically.
     */
    const collapsedSlotIdxByParent = useMemo(() => {
        const map = new Map<SubsectionId, number>();
        for (const node of tree) {
            const sub = node.subsection;
            if (sub.slotsAsItems) continue;
            if (node.children.length > 0) continue;
            const visibleIndices: number[] = [];
            sub.slots.forEach((slot, idx) => {
                if (isSlotVisible(slot, triggers)) visibleIndices.push(idx);
            });
            if (visibleIndices.length === 1) {
                map.set(sub.id, visibleIndices[0]);
            }
        }
        return map;
    }, [tree, triggers]);

    /**
     * The next incomplete row after the one in focus, walking the rail in
     * display order (CATEGORY_ORDER → within-category natural order →
     * sub-rows under their section). Threaded into each Save-style form so
     * the post-save CTA flips from "Saved" to "Continue to {title} →" —
     * keeps the applicant moving without forcing them back to the left
     * list to find the next step. Returns null when nothing after the
     * current row is outstanding, so the CTA falls back to the passive
     * "Saved" affordance.
     */
    const nextIncompleteTarget = useMemo<NextStepTarget | null>(() => {
        if (!focusedNode) return null;
        const ordered: OnboardingNode[] = [];
        for (const cat of CATEGORY_ORDER) {
            const items = grouped.get(cat);
            if (items) ordered.push(...items);
        }
        const focusedId = focusedNode.subsection.id;
        return findNextIncompleteTarget(
            ordered,
            focusedSlot?.parentId === focusedId
                ? { id: focusedId, slotIdx: focusedSlot.slotIdx }
                : { id: focusedId },
            triggers,
            collapsedSlotIdxByParent
        );
    }, [focusedNode, focusedSlot, grouped, triggers, collapsedSlotIdxByParent]);

    const onSelectSubsection = (id: SubsectionId): void => {
        setFocusedId(id);
        // If this subsection auto-collapses to a single slot, focus the
        // slot directly so the right panel renders the slot detail.
        const idx = collapsedSlotIdxByParent.get(id);
        setFocusedSlot(
            idx !== undefined ? { parentId: id, slotIdx: idx } : null
        );
    };

    const onSelectSlot = (parentId: SubsectionId, slotIdx: number): void => {
        setFocusedId(parentId);
        setFocusedSlot({ parentId, slotIdx });
    };

    // Right-pane handlers — extracted from inline JSX so the SlotDetailPanel
    // and SubsectionDetailPanel call sites stay scannable.
    const handleDeleteWallet = useCallback((idx: number): void => {
        // Splice the matching position out of the local UI cache.
        // The underlying Participant Account row stays put (no client-driven
        // wallet-delete API today); this is a UX-only removal of the
        // synthetic slot. When/if the BE adds a delete endpoint, the
        // wallet_declarations step would auto-revert via a sibling
        // cross-service call (mirrors Phase C's auto-complete).
        setWalletEntries((prev) => prev.filter((_, i) => i !== idx));
        setFocusedSlot(null);
    }, []);

    const handleDeletePerson = useCallback((idx: number): void => {
        // PersonDetailForm posts the entries[] without this entryUuid
        // before invoking this callback, so the BE row is already gone.
        // Splice by position so deleting person 1 of 3 leaves persons
        // 2 + 3 aligned with the BE.
        setUboEntries((prev) => prev.filter((_, i) => i !== idx));
        setFocusedSlot(null);
    }, []);

    const handleUboNameChange = useCallback(
        (idx: number, name: string): void => {
            setUboEntries((prev) =>
                prev.map((entry, i) =>
                    i === idx ? { ...entry, legalName: name } : entry
                )
            );
        },
        []
    );

    const handleWalletLabelChange = useCallback(
        (idx: number, label: string): void => {
            setWalletEntries((prev) =>
                prev.map((entry, i) =>
                    i === idx ? { ...entry, label } : entry
                )
            );
        },
        []
    );

    const handleWalletVerified = useCallback(
        (idx: number, wallet: WalletView): void => {
            setWalletEntries((prev) => replaceWalletEntry(prev, idx, wallet));
        },
        []
    );

    const handleMarkComplete = useCallback((id: SubsectionId): void => {
        setUserCompletedIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);

    /**
     * Continue-CTA handler — focuses the next incomplete row, which is a
     * sub-row when the section in focus still has one outstanding.
     * Section targets go through `onSelectSubsection` so the
     * auto-collapse-into-slot rule (e.g. `tax-identification` opens
     * directly into its single upload slot) still fires.
     */
    const handleContinue = (): void => {
        if (!nextIncompleteTarget) return;
        if (nextIncompleteTarget.slotIdx !== undefined) {
            onSelectSlot(nextIncompleteTarget.id, nextIncompleteTarget.slotIdx);
            return;
        }
        onSelectSubsection(nextIncompleteTarget.id);
    };

    // EARMARK — the +Add Person and +Add Wallet flows below are
    // demo-only. They push synthetic slots whose data is never persisted
    // and assume the applicant typing in a UBO's name / a wallet label
    // is enough.
    //
    // Open design question: how should an applicant enter UBO + wallet
    // details for individuals/addresses that don't (and shouldn't) have
    // an account in our system? The current synthetic slot UI conflates
    // "data entry for a related entity" with "navigate into an account-
    // owned form". See claude-docs/intents/yaas-client/onboarding.md
    // for the wallet declaration and per-instance proof-point boundaries.
    //
    // Wallets / Beneficiaries: each Begin click is "+ Add another" —
    // push a new synthetic slot, then focus it. We can't read the new
    // index from React state synchronously, so we derive it from the
    // current count (= the index about to exist).
    const handleBegin = useCallback((): void => {
        if (!focusedNode) return;
        if (focusedNode.subsection.id === "wallet-primary") {
            // Push a draft sentinel entry so the synthetic slot exists
            // and WalletForm mounts at the new index. The sentinel's
            // walletUuid is empty until the verification ceremony
            // persists a real Participant Account — at which point the
            // wallet_declarations step flips Complete, our refetch
            // effect re-runs fetchClientWallets, and the sentinel is
            // replaced with the server-side row. Without this push,
            // subsection.slots[newIdx] is undefined and SlotDetailPanel
            // renders EmptyDetailPanel instead of WalletForm — a
            // regression introduced when Phase F collapsed the parallel
            // walletCount / walletLabels arrays into walletEntries.
            const newIdx = walletEntries.length;
            setWalletEntries((prev) => [...prev, { wallet: null, label: "" }]);
            setFocusedSlot({ parentId: "wallet-primary", slotIdx: newIdx });
            onOpen("wallet-primary");
            return;
        }
        if (focusedNode.subsection.id === "beneficial-ownership") {
            const newIdx = uboEntries.length;
            // crypto.randomUUID exists in all evergreen browsers; older
            // targets fall back to a timestamp+random string.
            const fresh =
                typeof crypto !== "undefined" &&
                typeof crypto.randomUUID === "function"
                    ? crypto.randomUUID()
                    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            setUboEntries((prev) => [
                ...prev,
                { entryUuid: fresh, legalName: "" }
            ]);
            setFocusedSlot({
                parentId: "beneficial-ownership",
                slotIdx: newIdx
            });
            onOpen("beneficial-ownership");
            return;
        }
        // Focus the first visible slot of this subsection (or first
        // child slot if the subsection has no own slots).
        const firstSlot = firstSlotFocus(
            focusedNode,
            triggers,
            slotNumberByKey
        );
        if (firstSlot) {
            setFocusedSlot(firstSlot);
        } else {
            onOpen(focusedNode.subsection.id);
        }
    }, [
        focusedNode,
        walletEntries.length,
        uboEntries.length,
        triggers,
        slotNumberByKey,
        onOpen
    ]);

    const list = (
        <div>
            {((): ReactElement[] => {
                const blocks: ReactElement[] = [];
                let firstRendered = true;
                for (const cat of CATEGORY_ORDER) {
                    const items = grouped.get(cat);
                    if (!items || items.length === 0) continue;
                    blocks.push(
                        <section
                            key={cat}
                            className={
                                firstRendered
                                    ? undefined
                                    : "pt-4 mt-4 border-t border-line-base"
                            }
                        >
                            <ul className="divide-y divide-line-base">
                                {items.map((node) => (
                                    <li
                                        key={node.subsection.id}
                                        className="py-1"
                                    >
                                        <SubsectionGroupRows
                                            node={node}
                                            ordinal={
                                                ordinalById.get(
                                                    node.subsection.id
                                                ) ?? 0
                                            }
                                            applicationFrozen={
                                                applicationFrozen
                                            }
                                            applicationPhase={applicationPhase}
                                            triggers={triggers}
                                            slotNumberByKey={slotNumberByKey}
                                            focusedId={focusedId}
                                            focusedSlot={focusedSlot}
                                            collapsedSlotIdxByParent={
                                                collapsedSlotIdxByParent
                                            }
                                            celebratingIds={celebratingIds}
                                            onSelectSubsection={
                                                onSelectSubsection
                                            }
                                            onSelectSlot={onSelectSlot}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </section>
                    );
                    firstRendered = false;
                }
                return blocks;
            })()}
        </div>
    );

    const rightPaneContent = (
        <>
            {rightOverride ? (
                rightOverride
            ) : focusedNode ? (
                focusedSlot ? (
                    <SlotDetailPanel
                        node={focusedNode}
                        applicationFrozen={applicationFrozen}
                        applicationPhase={applicationPhase}
                        focusedSlot={focusedSlot}
                        slotNumberByKey={slotNumberByKey}
                        onDeleteWallet={handleDeleteWallet}
                        onDeletePerson={handleDeletePerson}
                        uboEntries={uboEntries}
                        onUboNameChange={handleUboNameChange}
                        walletEntries={walletEntries}
                        onWalletLabelChange={handleWalletLabelChange}
                        onWalletVerified={handleWalletVerified}
                        nextStep={nextIncompleteTarget}
                        onContinue={handleContinue}
                    />
                ) : (
                    <SubsectionDetailPanel
                        node={focusedNode}
                        applicationFrozen={applicationFrozen}
                        applicationPhase={applicationPhase}
                        displayNumber={((): string | null => {
                            const ord = ordinalById.get(
                                focusedNode.subsection.id
                            );
                            if (ord !== undefined) return `${ord}.`;
                            return (
                                slotNumberByKey.get(
                                    focusedNode.subsection.id
                                ) ?? null
                            );
                        })()}
                        onOpen={onOpen}
                        onBegin={handleBegin}
                        jumpToCard={onSelectSubsection}
                        onMarkComplete={handleMarkComplete}
                        nextStep={nextIncompleteTarget}
                        onContinue={handleContinue}
                        walletEntries={walletEntries}
                    />
                )
            ) : (
                <EmptyDetailPanel />
            )}
        </>
    );

    return (
        <SplitLayout
            stackBelow="lg"
            columns={[
                {
                    size: 45,
                    // `pb-6` on both columns: the grid's bottom edge is
                    // where the submit section's rule lands, so without it
                    // the last control in the taller column sits flush
                    // against that rule.
                    className: "pt-4 pb-6 lg:pr-4",
                    content: list
                },
                {
                    size: 55,
                    className: "hidden lg:block lg:pt-4 lg:pb-6 lg:pl-4",
                    content: rightPaneContent
                }
            ]}
        />
    );
}

/**
 * Find the first visible slot of a focused subsection — used by the Begin
 * CTA on the subsection detail panel to advance into a slot view. Walks the
 * subsection's own slots first, then any nested children's slots.
 */
function firstSlotFocus(
    node: OnboardingNode,
    triggers: Record<string, boolean> | undefined,
    slotNumberByKey: Map<string, string>
): { parentId: SubsectionId; slotIdx: number } | null {
    for (let i = 0; i < node.subsection.slots.length; i++) {
        if (!isSlotVisible(node.subsection.slots[i], triggers)) continue;
        if (slotNumberByKey.has(slotKey(node.subsection.id, i))) {
            return { parentId: node.subsection.id, slotIdx: i };
        }
    }
    for (const child of node.children) {
        for (let i = 0; i < child.slots.length; i++) {
            if (!isSlotVisible(child.slots[i], triggers)) continue;
            if (slotNumberByKey.has(slotKey(child.id, i))) {
                return { parentId: child.id, slotIdx: i };
            }
        }
    }
    return null;
}
